import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";

import { onBrowser } from "../../helpers/onBrowser";
import { AlertText } from "../Alert";

import { Toast } from "./Toast";
import { ToastOverlay } from "./ToastOverlay";

let id = 0;
const TOAST_DEFAULT_TIMEOUT = 5000;

export const TOAST_TYPES = {
  ERROR: 0,
  INFO: 1,
  SUCCESS: 2,
  WARNING: 3,
} as const;

export type ToastType = (typeof TOAST_TYPES)[keyof typeof TOAST_TYPES];

interface ToastItem {
  id: number;
  type: ToastType;
  content: React.ReactNode;
  timeout?: number;
  going: boolean;
  shownTime: number;
}

function isHoveredOverToastOverlay(): boolean {
  const hovered = document.querySelectorAll(":hover");
  for (let i = 0; i < hovered.length; i++) {
    if (hovered[i].getAttribute("id") === "toast-overlay") {
      return true;
    }
  }
  return false;
}

/**
 * The below block allows us to export a ToastContext and cache it within the
 * node module registry without initializing it twice. If we initialize the
 * context twice, the context cached within the `ToastProvider` cached within
 * Confetti will be overridden by the one created within Kazoo...
 */
export function CreateOrUseExistingToastContext(
  initialContext: ToastContextType
): React.Context<ToastContextType> {
  if (!onBrowser()) {
    return createContext<ToastContextType>(initialContext);
  }

  if (!window.kazoo) {
    // TODO: WT-24033 the window kazoo type is technically incorrect
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    window.kazoo = {} as any;
  }

  const context = window.kazoo.toastContext;
  if (context) {
    return context;
  }

  window.kazoo.toastContext = createContext<ToastContextType>(initialContext);
  return window.kazoo.toastContext;
}

// Add type definitions for the global window object
const placeholderFunction = (): never => {
  throw new Error(
    `You tried to call a Toast placeholder function, meaning the Toast hasn't been set in the context. Did you forget to setup the Toast provider? Or maybe you meant to mock this function?`
  );
};

export interface ToastContextType {
  error: (content: React.ReactNode) => void;
  info: (content: React.ReactNode, timeout?: number) => void;
  success: (content: React.ReactNode, timeout?: number) => void;
  warning: (content: React.ReactNode) => void;
}

const ToastContext = CreateOrUseExistingToastContext({
  error: placeholderFunction,
  info: placeholderFunction,
  success: placeholderFunction,
  warning: placeholderFunction,
});

/**
 * @typedef {object} ToastSuite
 * @prop {(content?: React.ReactNode, timeout?: number) => void} success
 * @prop {(content?: React.ReactNode) => void} error
 * @prop {(content?: React.ReactNode) => void} warning
 * @prop {(content?: React.ReactNode, timeout?: number) => void} info
 */

/**
 * @returns {ToastSuite} A suite of functions to call for displaying a popup Toast with text.
 */
export const useToast = (): ToastContextType => useContext(ToastContext);

function addToast(
  type: ToastType,
  setToasts: React.Dispatch<React.SetStateAction<ToastItem[]>>,
  content: React.ReactNode,
  timeout?: number
): void {
  setToasts((currentToasts) => [
    ...currentToasts,
    { id: id++, type, content, timeout, going: false, shownTime: Date.now() },
  ]);
}

function removeToast(
  setToasts: React.Dispatch<React.SetStateAction<ToastItem[]>>,
  toastId: number
): void {
  setToasts((currentToasts) =>
    currentToasts.filter((toast) => toast.id !== toastId)
  );
}

function addSuccess(
  setToasts: React.Dispatch<React.SetStateAction<ToastItem[]>>,
  content: React.ReactNode,
  timeout?: number
): void {
  addToast(TOAST_TYPES.SUCCESS, setToasts, content, timeout);
}

function addWarning(
  setToasts: React.Dispatch<React.SetStateAction<ToastItem[]>>,
  content: React.ReactNode
): void {
  addToast(TOAST_TYPES.WARNING, setToasts, content);
}

function addError(
  setToasts: React.Dispatch<React.SetStateAction<ToastItem[]>>,
  content: React.ReactNode
): void {
  addToast(TOAST_TYPES.ERROR, setToasts, content);
}

function addInfo(
  setToasts: React.Dispatch<React.SetStateAction<ToastItem[]>>,
  content: React.ReactNode,
  timeout?: number
): void {
  addToast(TOAST_TYPES.INFO, setToasts, content, timeout);
}

interface ToastProviderProps {
  children: ReactNode;
  defaultTimeout?: number;
  text?: AlertText;
  /**
   * Optional CSS value for the top position of the toast overlay.
   *
   * Use this to position toasts below fixed headers or other UI elements that might
   * obscure them. Accepts any valid CSS top value (e.g., "80px", "5rem", "10%").
   *
   * @example
   * ```tsx
   * // Position toasts 80px from the top, below a fixed header
   * <ToastProvider top="80px">
   *   <YourApp />
   * </ToastProvider>
   * ```
   *
   * @default undefined (uses the default top position from Toast.scss)
   */
  top?: string;
}

export function ToastProvider({
  children,
  defaultTimeout = TOAST_DEFAULT_TIMEOUT,
  text = undefined,
  top,
}: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    // Ignore this if we're on the server
    if (!onBrowser()) {
      return;
    }
    // This is our timer that fires every 500ms and removes stale
    // toasts from the screen.
    const interval = setInterval(() => {
      const currentTime = Date.now();
      const mouseOrFocusOnOverlay = isHoveredOverToastOverlay();
      // For ensuring we don't needlessly update state
      let changed = false;
      const newToasts = toasts
        .filter((toast) => !toast.going)
        .map((toast) => {
          if (mouseOrFocusOnOverlay) {
            return toast;
          }
          if (toast.timeout) {
            if (currentTime - toast.shownTime > toast.timeout) {
              // Remove it if it's past the amount of time we want
              // to show the toast.
              toast.going = true;
              changed = true;
            }
          }
          return toast;
        });
      // Boost our shown time a bit if the user is hovering over the
      // toast so it doesn't go away immediately
      if (mouseOrFocusOnOverlay) {
        newToasts.forEach((_, i) => (newToasts[i].shownTime += 600));
      }
      // If any of our toasts were removed or changed, update the state
      if (changed || newToasts.length !== toasts.length) {
        setToasts(newToasts);
      }
    }, 500);
    return () => {
      clearInterval(interval);
    };
  }, [toasts]);

  const errorFn = React.useCallback(
    (content: React.ReactNode) => addError(setToasts, content),
    [setToasts]
  );

  const infoFn = React.useCallback(
    (content: React.ReactNode, timeout = defaultTimeout) =>
      addInfo(setToasts, content, timeout),
    [setToasts, defaultTimeout]
  );

  const successFn = React.useCallback(
    (content: React.ReactNode, timeout = defaultTimeout) =>
      addSuccess(setToasts, content, timeout),
    [setToasts, defaultTimeout]
  );

  const warningFn = React.useCallback(
    (content: React.ReactNode) => addWarning(setToasts, content),
    [setToasts]
  );

  const contextValue = React.useMemo(
    () => ({
      error: errorFn,
      info: infoFn,
      success: successFn,
      warning: warningFn,
    }),
    [errorFn, infoFn, successFn, warningFn]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      <ToastOverlay top={top}>
        {toasts.map((toast) => {
          const props = {
            content: toast.content,
            text,
            dismiss: () => removeToast(setToasts, toast.id),
            going: toast.going,
            key: toast.id,
            timeout: toast.timeout,
            type: toast.type,
          };
          switch (toast.type) {
            case TOAST_TYPES.ERROR:
              return <Toast variant="warning" {...props} />;
            case TOAST_TYPES.SUCCESS:
              return <Toast variant="success" {...props} />;
            case TOAST_TYPES.WARNING:
              return <Toast variant="warning" {...props} />;
            default:
              return <Toast variant="info" {...props} />;
          }
        })}
      </ToastOverlay>
      {children}
    </ToastContext.Provider>
  );
}
